/* ═══════════════════════════════════════════════════════════════════════════
   FF Also Available On — Compact pills cross-sell
   Fetches products sharing the same design: tag via Section Rendering API,
   deduplicates by taxonomy category name (falling back to product type),
   and renders one pill per unique label.
   Created: Feb 18 2026
   ═══════════════════════════════════════════════════════════════════════════ */

if (!customElements.get('ff-also-available-on')) {
  class FFAlsoAvailableOn extends HTMLElement {
    /**
     * ── Singularizer exception sets ──────────────────────────────
     * Built from a full audit of Shopify's 12,000+ standard taxonomy
     * categories (2026-02-unstable). Rules handle the common patterns;
     * these sets handle every edge case found in the taxonomy.
     */

    /* Words that must stay exactly as-is (inherently plural / uncountable) */
    static KEEP_PLURAL = new Set([
      'pants', 'shorts', 'glasses', 'sunglasses', 'overalls',
      'jeans', 'leggings', 'tights', 'briefs', 'boxers',
      'trousers', 'pajamas', 'binoculars', 'scissors', 'pliers',
      'shears', 'tweezers', 'goggles',
    ]);

    /* Words ending in "s" that are already singular — do not modify */
    static ALREADY_SINGULAR = new Set([
      'canvas', 'asparagus', 'couscous', 'hummus', 'hibiscus',
      'citrus', 'discus', 'tennis', 'kalangus', 'physalis',
      'surplus', 'apparatus', 'stylus', 'fungus', 'cactus',
      'rhinoceros', 'iris', 'debris', 'chassis', 'analysis',
    ]);

    /**
     * Words ending in "ies" where we ONLY drop "s" (→ "ie").
     * Default for "ies" words is ies→y, which is correct for the
     * vast majority of taxonomy entries (606 vs ~29).
     */
    static IES_DROP_S = new Set([
      'hoodies', 'beanies', 'cookies', 'onesies', 'smoothies',
      'brownies', 'plushies', 'neckties', 'headties', 'squeakies',
      'duckies', 'birdies', 'pixies', 'genies', 'collies',
      'rotisseries', 'caddies', 'lingeries',
    ]);

    /* Words ending in "ves" where we ONLY drop "s" (→ "ve").
     * Default for "lves" is ves→f (shelves→shelf, etc).  */
    static VES_DROP_S = new Set([
      'valves', 'olives', 'locomotives', 'adhesives', 'alternatives',
      'additives', 'preservatives', 'objectives', 'perspectives',
      'representatives', 'narratives', 'archives', 'initiatives',
      'atives', 'motives', 'relatives', 'directives', 'explosives',
      'incentives', 'negatives', 'positives', 'primitives',
      'sedatives', 'sensitives',
    ]);

    /**
     * Naïve singularizer — operates on the LAST word only.
     * Handles the most common English plural patterns found
     * in Shopify's product taxonomy. Audited against 12,134
     * unique category names from the 2026-02 taxonomy release.
     */
    static singularize(label) {
      if (!label) return label;

      const words = label.split(' ');
      const last = words[words.length - 1];
      const lower = last.toLowerCase();

      /* Skip words that are inherently plural or uncountable */
      if (FFAlsoAvailableOn.KEEP_PLURAL.has(lower)) return label;

      /* Skip words that are already singular */
      if (FFAlsoAvailableOn.ALREADY_SINGULAR.has(lower)) return label;

      /* Skip if not ending in "s" or if too short to be a plural */
      if (!lower.endsWith('s') || lower.length < 3) return label;

      /* Skip words ending in "ss" (e.g. "Glass", "Dress" — but "Dresses" is caught by "sses" below) */
      if (lower.endsWith('ss')) return label;

      let singular;

      if (lower.endsWith('sses')) {
        /* Dresses → Dress */
        singular = last.slice(0, -2);
      } else if (lower.endsWith('ches') || lower.endsWith('shes') || lower.endsWith('xes')) {
        /* Patches → Patch, Brushes → Brush, Boxes → Box */
        singular = last.slice(0, -2);
      } else if (lower.endsWith('ves')) {
        if (FFAlsoAvailableOn.VES_DROP_S.has(lower)) {
          /* Valves → Valve, Adhesives → Adhesive */
          singular = last.slice(0, -1);
        } else if (lower.endsWith('nives') || lower.endsWith('wives')) {
          /* Knives → Knife, Wives → Wife, Midwives → Midwife */
          singular = last.slice(0, -3) + 'fe';
        } else if (lower.endsWith('lves')) {
          /* Shelves → Shelf, Halves → Half, Calves → Calf */
          singular = last.slice(0, -3) + 'f';
        } else if (lower.endsWith('arves')) {
          /* Scarves → Scarf */
          singular = last.slice(0, -3) + 'f';
        } else if (lower.endsWith('oaves')) {
          /* Loaves → Loaf */
          singular = last.slice(0, -3) + 'f';
        } else if (lower.endsWith('eaves') && lower !== 'eaves') {
          /* Leaves → Leaf (but "eaves" stays) */
          singular = last.slice(0, -3) + 'f';
        } else {
          /* Gloves → Glove, Drives → Drive, Stoves → Stove */
          singular = last.slice(0, -1);
        }
      } else if (lower.endsWith('ies') && lower.length > 4) {
        if (FFAlsoAvailableOn.IES_DROP_S.has(lower)) {
          /* Hoodies → Hoodie, Beanies → Beanie, Cookies → Cookie */
          singular = last.slice(0, -1);
        } else {
          /* Default: ies → y (Batteries → Battery, Supplies → Supply, etc.) */
          singular = last.slice(0, -3) + 'y';
        }
      } else {
        /* Default: drop trailing "s" (Sweaters → Sweater, Caps → Cap) */
        singular = last.slice(0, -1);
      }

      words[words.length - 1] = singular;
      return words.join(' ');
    }

    connectedCallback() {
      this.designTag = this.dataset.designTag || '';
      this.currentHandle = this.dataset.currentHandle || '';

      if (!this.designTag) return;

      this.pillsContainer = this.querySelector('.ff-ao__pills');
      if (!this.pillsContainer) return;

      this._fetchSiblings();
    }

    async _fetchSiblings() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}collections/all/${this.designTag}?section_id=ff-also-available-on`;

        const res = await fetch(url);
        if (!res.ok) return;

        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('.ff-ao__item');

        if (!items.length) return;

        /* ── Build deduplicated label → url map (skip current product) ── */
        const typeMap = new Map();

        items.forEach((item) => {
          const handle = item.dataset.handle;
          const category = item.dataset.category; /* Shopify taxonomy (deepest level) */
          const type = item.dataset.type;          /* Legacy product type (fallback) */
          const itemUrl = item.dataset.url;

          if (handle === this.currentHandle) return;

          /* Prefer taxonomy category name; fall back to product type */
          const rawLabel = (category && category.trim()) ? category.trim() : (type || '');
          if (!rawLabel) return;
          const label = FFAlsoAvailableOn.singularize(rawLabel);

          /* First product of each label wins */
          if (!typeMap.has(label)) {
            typeMap.set(label, itemUrl);
          }
        });

        if (typeMap.size === 0) return;

        /* ── Render pills ─────────────────────────────────────────── */
        this.pillsContainer.innerHTML = '';

        typeMap.forEach((pillUrl, type) => {
          const a = document.createElement('a');
          a.href = pillUrl;
          a.className = 'ff-ao__pill';
          a.textContent = type;
          this.pillsContainer.appendChild(a);
        });

        this.classList.add('ff-ao--loaded');
      } catch (e) {
        console.warn('ff-also-available-on: fetch failed', e);
      }
    }
  }

  customElements.define('ff-also-available-on', FFAlsoAvailableOn);
}